import { Controller } from "stimulus";
import { SupercastTimeUpdateEvent, SupercastPlaybackEndedEvent } from "utils/events";
import { appSupportsComponent } from "utils/hotwire";
import { trackPlaybackStarted } from "utils/playback_analytics";
import { absoluteUrl } from "utils/absolute_url";
import "audio_skin";
import { readPlyrStorage, writePlyrStorage, nearestRate } from "utils/player_preferences";
import { claimMediaSession, ownsMediaSession } from "utils/media_session_owner";
import {
  mediaErrorCategory,
  sendPlaybackBreadcrumb,
  sendPlaybackError,
  signedUrlExpiryMs
} from "utils/playback_telemetry";

// Video.js v10 audio controller.
// Drop-in: same public API, so callers reach it through getAudioPlayerController().
//
// Drives the native <audio> inside <audio-player>, which Video.js adopts as its media provider.
//
// TODO: the visual controls.

// `stalled` is normal on a flaky connection, so breadcrumbs are capped per episode.
const maxStalledBreadcrumbsPerEpisode = 3;

const SEEK_SECONDS = 15;
const HAVE_METADATA = 1;

// What <media-playback-rate-button> cycles through when the store has not reported its own list.
const DEFAULT_PLAYBACK_RATES = [0.2, 0.5, 0.7, 1, 1.2, 1.5, 1.7, 2];

const MEDIA_SESSION_EVENTS = ["pause", "ended", "seeked", "ratechange", "loadedmetadata", "durationchange"];

export default class extends Controller {
  static targets = ["media"]

  static values = {
    timestamp: Number,
    itemId: String,
    itemType: String,
    mediaMetadata: Object,
    // :playback_observability. Only rendered when the flag is on; off means no listeners or reports.
    observability: Boolean,
    surface: String,
    viewerId: String,
    subPropertyId: String,
    planTier: String,
    audioUrlExpiresAtMs: Number,
    serverNowMs: Number
  }

  // The native <audio> element.
  get media() {
    return this.hasMediaTarget ? this.mediaTarget : this.element.querySelector("audio");
  }

  get paused() {
    return this.media?.paused ?? true;
  }

  connect() {
    const audio = this.media;
    if (!audio) return;

    // The fixed player is data-turbo-permanent: the <audio> node outlives the controller, so a
    // reconnect must not attach listeners or resume again.
    if (audio.supercastVideojsAudioBound) return;
    audio.supercastVideojsAudioBound = true;

    // The fixed player has no source until loadEpisode.
    if (audio.paused && audio.getAttribute("src")) {
      this.applyResume(audio);
    }

    if (!this.nativeOwnsPlayback()) trackPlaybackStarted(audio, "episode");
    audio.addEventListener("playing", () => this.onTimeUpdate());
    audio.addEventListener("pause", () => this.onTimeUpdate());
    audio.addEventListener("timeupdate", () => this.onTimeUpdate());
    audio.addEventListener("ended", () => this.playbackEnded());
    audio.addEventListener("volumechange", () => this.onVolumeChange());
    audio.addEventListener("ratechange", () => this.onRateChange());
    audio.addEventListener("loadedmetadata", () => this.reapplySpeed());
    // A failed load never reaches loadedmetadata.
    audio.addEventListener("error", () => { this.sourceLoading = false; this.pendingRateReset = false; });

    this.restorePreferences(audio);
    this.setupMediaSession(audio);

    this.observePlayback(audio);
  }

  loadEpisode({ itemId, itemType, audioUrl, timestamp, mediaMetadata }) {
    this.itemIdValue = itemId;
    this.itemTypeValue = itemType;

    const audio = this.media;

    // Absolute, to compare equal to the element's own src.
    const src = absoluteUrl(audioUrl);

    // The only place the played URL, and so its expiry, is visible.
    this.observeAudioUrl(src);

    if (audio.src != src) {
      this.markSourceLoading();
      audio.src = src;
    }

    if (timestamp != null) {
      this.timestampValue = timestamp;
    }

    if (mediaMetadata) {
      this.mediaMetadataValue = {
        title: mediaMetadata.title,
        artist: mediaMetadata.artist,
        album: mediaMetadata.album,
        artwork: mediaMetadata.artworkUrl
          ? [{ src: mediaMetadata.artworkUrl, sizes: "512x512" }]
          : this.mediaMetadataValue.artwork
      };
    }

    this.updateMediaSessionMetadata();
    this.applyResume(audio);
    this.play();

    this.updateFeedTokenConnection();
  }

  updateFeedTokenConnection() {
    const episodeId = this.itemIdValue;
    if (episodeId) {
      fetch(`/subscriber_v2/feed_items/mark_feed_token_connected?episode_id=${episodeId}`, {
        method: "POST"
      });
    }
  }

  onTimeUpdate() {
    const audio = this.media;
    const event = new SupercastTimeUpdateEvent({
      itemId: this.itemIdValue,
      itemType: this.itemTypeValue,
      currentTime: audio.currentTime,
      // NaN until metadata loads.
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
      isPlaying: !audio.paused,
      mediaType: "audio"
    });

    window.dispatchEvent(event);
  }

  playbackEnded() {
    const event = new SupercastPlaybackEndedEvent({
      itemId: this.itemIdValue,
      itemType: this.itemTypeValue,
      mediaType: "audio"
    });

    window.dispatchEvent(event);
  }

  play() {
    console.log("Media Playback: play");
    // Rejects when a newer load or a pause interrupts it; not worth surfacing.
    this.media.play()?.catch(() => {});
  }

  pause() {
    this.media.pause();
  }

  jumpBack() {
    this.seek(this.media.currentTime - SEEK_SECONDS);
  }

  jumpForward() {
    this.seek(this.media.currentTime + SEEK_SECONDS);
  }

  toggleMute() {
    this.media.muted = !this.media.muted;
  }

  onVolumeChange() {
    const { volume, muted } = this.media;
    writePlyrStorage({ volume, muted });
    window.dispatchEvent(new CustomEvent("supercast-mute-change", { detail: { muted } }));
  }

  seek(time) {
    this.media.currentTime = time;
  }

  // --- Playback rate ---
  // Video.js offers a fixed set of rates, so a saved 0.9 or 1.1 snaps to the nearest one.
  //
  // Assigning a new src runs the media load algorithm, which resets playbackRate to 1 and fires a
  // ratechange. So the chosen rate is re-applied on loadedmetadata, and ratechanges are ignored
  // until then: the reset to the default rate is never persisted or announced, but a rate the
  // viewer picks while loading is.

  get playbackRates() {
    const rates = this.element.querySelector("audio-player")?.store?.state?.playbackRates;
    return rates?.length ? rates : DEFAULT_PLAYBACK_RATES;
  }

  // Same order as the stock rate button: the next rate up, wrapping to the first.
  togglePlaybackRate() {
    const rates = this.playbackRates;
    const current = this.media.playbackRate;
    this.media.playbackRate = rates.find((rate) => rate > current) ?? rates[0];
  }

  onRateChange() {
    const speed = this.media.playbackRate;
    if (this.pendingRateReset && speed === this.media.defaultPlaybackRate) {
      this.pendingRateReset = false;
      return;
    }

    this.intendedRate = speed;
    writePlyrStorage({ speed });
    window.dispatchEvent(new CustomEvent("supercast-rate-change", { detail: { playbackRate: speed } }));
  }

  // Called when a new source is about to be assigned.
  markSourceLoading() {
    this.sourceLoading = true;
    // Loading from the default rate fires no reset, so there is nothing to ignore then.
    this.pendingRateReset = this.media.playbackRate !== this.media.defaultPlaybackRate;
  }

  reapplySpeed() {
    if (!this.sourceLoading) return;
    this.sourceLoading = false;
    this.pendingRateReset = false;
    if (this.intendedRate && this.media.playbackRate !== this.intendedRate) {
      this.media.playbackRate = this.intendedRate;
    }
  }

  // --- Saved preferences ---

  restorePreferences(audio) {
    const saved = readPlyrStorage();
    if (!saved) return;

    const speed = Number(saved.speed);
    const volume = Number(saved.volume);
    try {
      if (speed > 0) {
        this.intendedRate = nearestRate(this.playbackRates, speed);
        audio.playbackRate = this.intendedRate;
      }
      if (saved.volume != null && Number.isFinite(volume) && volume >= 0 && volume <= 1) audio.volume = volume;
      if (saved.muted === true) audio.muted = true;
    } catch {
      // volume is not controllable here (e.g. iOS) - keep the defaults
    }
  }

  // The playback target picker is WebKit-only; a no-op elsewhere.
  showAirplay() {
    this.media.webkitShowPlaybackTargetPicker?.();
  }

  // --- Media Session ---
  // Shared with the video player: it claims the session when it plays, and this claims it back.
  // The handlers are registered inside a play event, never at connect, so a native player that owns
  // playback (Android/iOS WebView) never sees two claimants on now-playing.

  setupMediaSession(audio) {
    if (!("mediaSession" in navigator)) return;

    audio.addEventListener("play", () => {
      this.claimMediaSession();
      this.updateMediaSessionPlaybackState();
    });
    MEDIA_SESSION_EVENTS.forEach((name) => audio.addEventListener(name, () => this.updateMediaSessionPlaybackState()));
  }

  claimMediaSession() {
    const session = navigator.mediaSession;
    claimMediaSession(this);
    this.updateMediaSessionMetadata();

    const handlers = {
      play: () => this.play(),
      pause: () => this.pause(),
      seekbackward: (details) => this.seek(this.media.currentTime - (details?.seekOffset || SEEK_SECONDS)),
      seekforward: (details) => this.seek(this.media.currentTime + (details?.seekOffset || SEEK_SECONDS))
    };
    Object.entries(handlers).forEach(([action, handler]) => {
      try { session.setActionHandler(action, handler); } catch { /* action unsupported */ }
    });
  }

  updateMediaSessionMetadata() {
    const { title, artist, album, artwork } = this.mediaMetadataValue;
    if (!ownsMediaSession(this) || !title || typeof MediaMetadata !== "function") return;

    navigator.mediaSession.metadata = new MediaMetadata({ title, artist, album, artwork });
  }

  updateMediaSessionPlaybackState() {
    if (!ownsMediaSession(this)) return;

    const session = navigator.mediaSession;
    const { duration, currentTime, playbackRate, paused } = this.media;

    // setPositionState throws on a non-finite duration or a position past the duration.
    if (Number.isFinite(duration) && duration > 0) {
      try {
        session.setPositionState({
          duration,
          playbackRate: playbackRate || 1,
          position: Math.min(Math.max(currentTime || 0, 0), duration)
        });
      } catch { /* invalid position state */ }
    }
    session.playbackState = paused ? "paused" : "playing";
  }

  // Applies the saved position once metadata is loaded; earlier sets can be dropped after a src change.
  async applyResume(audio) {
    const { itemId, itemType } = { itemId: this.itemIdValue, itemType: this.itemTypeValue };
    // A deferred apply from a previous episode must not land on this one.
    audio.removeEventListener("loadedmetadata", audio.supercastPendingResume);
    const time = await this.resumeTime();
    if (time == null) return;
    // Another episode loaded during the lookup.
    if (itemId !== this.itemIdValue || itemType !== this.itemTypeValue) return;

    audio.removeEventListener("loadedmetadata", audio.supercastPendingResume);
    const apply = () => { audio.currentTime = time; };
    if (audio.readyState >= HAVE_METADATA) {
      apply();
    } else {
      audio.supercastPendingResume = apply;
      audio.addEventListener("loadedmetadata", apply, { once: true });
    }
  }

  async resumeTime() {
    if (this.hasTimestampValue && this.timestampValue > 0) {
      return this.timestampValue;
    }

    if (window.playbackDb) {
      const listenData = await window.playbackDb.getPlaybackData(this.itemTypeValue, this.itemIdValue);
      if (listenData) {
        const shouldRestart = listenData.duration - listenData.currentTime < SEEK_SECONDS;
        return shouldRestart ? 0 : listenData.currentTime;
      }
    }

    return null;
  }

  // :playback_observability. Instrumentation only: no retry, fallback or error UI.

  observePlayback(audio) {
    if (!this.observabilityValue || !audio) return;
    if (this.nativeOwnsPlayback()) return;

    try {
      const state = this.playbackState();
      // serverNowMs is a render-time reading, so this is the skew at connect.
      state.clockSkewMs = this.hasServerNowMsValue ? this.serverNowMsValue - Date.now() : 0;
      state.tokenExpiresAtMs = this.hasAudioUrlExpiresAtMsValue && this.audioUrlExpiresAtMsValue > 0
        ? this.audioUrlExpiresAtMsValue
        : null;

      audio.addEventListener("error", () => this.onMediaError());
      audio.addEventListener("stalled", () => this.onMediaStalled());
    } catch {
      // A player that reports nothing is strictly better than a player that doesn't play.
    }
  }

  // True when the native player, not this one, is producing sound. Not a bare [data-hotwire-native]
  // check: binaries without the play-button component fall back to this web player.
  nativeOwnsPlayback() {
    return !!document.querySelector("[data-hotwire-native]") && appSupportsComponent("play-button");
  }

  // Kept on the element, not the instance: the permanent <audio> outlives the controller its
  // listeners were bound to.
  playbackState() {
    const audio = this.media;
    if (!audio) return {};

    if (!audio.supercastPlaybackState) {
      audio.supercastPlaybackState = { clockSkewMs: 0, tokenExpiresAtMs: null, tokenLifetimeMs: null, stalls: 0 };
    }

    return audio.supercastPlaybackState;
  }

  // Records the expiry of the URL about to be played.
  observeAudioUrl(audioUrl) {
    if (!this.observabilityValue) return;

    try {
      const state = this.playbackState();
      state.tokenExpiresAtMs = signedUrlExpiryMs(audioUrl);
      state.tokenLifetimeMs = state.tokenExpiresAtMs === null ? null : state.tokenExpiresAtMs - this.now();
      state.stalls = 0;
    } catch {
      // Leave the previous episode's expiry in place rather than asserting a wrong one.
    }
  }

  // One report per failed load.
  onMediaError() {
    const audio = this.media;
    const mediaError = audio && audio.error;
    const code = mediaError && mediaError.code;

    this.reportPlaybackError("Audio playback failed", {
      media_error_code: code,
      media_error_category: mediaErrorCategory(code),
      // Vendor-specific and often empty.
      media_error_message: mediaError && mediaError.message,
      network_state: audio && audio.networkState,
      ready_state: audio && audio.readyState,
      // Start vs. mid-stream separates a broken asset from an expiring credential.
      position_seconds: audio && Number.isFinite(audio.currentTime) ? Math.round(audio.currentTime) : null,
      stalls: this.playbackState().stalls
    });
  }

  onMediaStalled() {
    if (!this.observabilityValue) return;

    const state = this.playbackState();
    state.stalls = (state.stalls || 0) + 1;
    if (state.stalls > maxStalledBreadcrumbsPerEpisode) return;

    const audio = this.media;

    this.addPlaybackBreadcrumb("stalled", {
      attempt: state.stalls,
      ready_state: audio && audio.readyState,
      position_seconds: audio && Number.isFinite(audio.currentTime) ? Math.round(audio.currentTime) : null,
      ...this.tokenExpiryTags()
    });
  }

  // Tells an expired credential from a dropped connection (both are MEDIA_ERR_NETWORK).
  // "unknown" is common (redirect routes, feed-token URLs have no visible expiry), so it must not
  // default to "expired".
  tokenExpiryTags() {
    const state = this.playbackState();

    if (state.tokenExpiresAtMs === null || state.tokenExpiresAtMs === undefined) {
      return { token_expiry: "unknown" };
    }

    const remainingSeconds = Math.round((state.tokenExpiresAtMs - this.now()) / 1000);

    return {
      token_expiry: remainingSeconds <= 0 ? "expired" : "valid",
      token_expires_in_s: remainingSeconds,
      token_lifetime_s: state.tokenLifetimeMs === null || state.tokenLifetimeMs === undefined
        ? null
        : Math.round(state.tokenLifetimeMs / 1000)
    };
  }

  // Server time, so a wrong device clock doesn't misreport expiry.
  now() {
    return Date.now() + (this.playbackState().clockSkewMs || 0);
  }

  // Context for every report, matching the video player.
  playbackTags() {
    return {
      media_type: "audio",
      surface: this.surfaceValue,
      plan_tier: this.planTierValue,
      sub_property_id: this.subPropertyIdValue,
      viewer_id: this.viewerIdValue,
      item_id: this.itemIdValue,
      item_type: this.itemTypeValue,
      // "native" here means a binary that fell back to web playback.
      client_surface: document.querySelector("[data-hotwire-native]") ? "native" : "web",
      ...this.tokenExpiryTags()
    };
  }

  addPlaybackBreadcrumb(action, metadata = {}) {
    if (!this.observabilityValue) return;

    sendPlaybackBreadcrumb("audio_player", action, {
      ...metadata,
      item_id: this.itemIdValue,
      surface: this.surfaceValue
    });
  }

  reportPlaybackError(message, tags = {}) {
    if (!this.observabilityValue) return undefined;

    return sendPlaybackError(message, {
      action: "media--audio-player",
      tags: { ...this.playbackTags(), ...tags }
    });
  }
}
